import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { format, intervalToDuration } from "date-fns";
import { z } from "zod";
import { ArrowLeft, ChevronDown, Copy, Info, Loader2, RotateCcw, Square, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { supportsCustomFieldMappings } from "@/components/itsm-import/custom-field-mappings";
import { CustomFieldMappingsSummary } from "@/components/itsm-import/custom-field-mappings-summary";
import { ExportCsvSection } from "@/components/itsm-import/export-csv-section";
import { ImportTicketsList } from "@/components/itsm-import/import-tickets-list";
import { JobStatusBadge } from "@/components/itsm-import/job-status-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useImportJob, useCancelImportJob, useRetryImportJob } from "@/hooks/use-itsm-import";
import { useIntegrationSyncLinkedTickets } from "@/hooks/use-itsm-config";
import { useAllTenants } from "@/hooks/use-tenants";
import { ACTIVE_STATUSES } from "@/lib/types/itsm-import";
import type { ImportJobResponse } from "@/lib/types/itsm-import";

const searchSchema = z.object({
  tenantId: z.string(),
});

export const Route = createFileRoute("/itsm-import_/$jobId")({
  component: JobDetailPage,
  validateSearch: zodValidator(searchSchema),
});

function JobDetailPage() {
  const { jobId } = Route.useParams();
  const { tenantId } = Route.useSearch();
  const { data: job, isLoading } = useImportJob(tenantId, jobId);
  const { data: tenants } = useAllTenants();
  const navigate = useNavigate();
  const cancelJob = useCancelImportJob(tenantId);
  const tenantName = tenants?.find((t) => t.id === tenantId)?.company;
  const retryJob = useRetryImportJob(tenantId);

  const handleCancel = async () => {
    try {
      await cancelJob.mutateAsync(jobId);
      toast.success("Import job cancelled");
    } catch {
      toast.error("Failed to cancel import job");
    }
  };

  const handleRetry = async () => {
    try {
      await retryJob.mutateAsync(jobId);
      toast.success("Import job retried");
    } catch {
      toast.error("Failed to retry import job");
    }
  };

  const handleClone = () => {
    void navigate({ to: "/itsm-import/new", search: { tenantId, cloneFrom: jobId } });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="container mx-auto max-w-5xl p-6">
        <p className="text-muted-foreground">Job not found.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-5xl space-y-6 p-6">
      {/* Back link */}
      <Link
        to="/itsm-import"
        search={{ tenantId }}
        className="-ml-2 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Jobs
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Import Job</h1>
          <JobStatusBadge status={job.status} />
          {job.dry_run && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200">
              DRY RUN
            </span>
          )}
          {job.export_csv && (
            <span className="rounded bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-900 dark:text-sky-200">
              CSV EXPORT
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(job.status === "completed" || job.status === "failed" || job.status === "cancelled") && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { handleClone(); }}
            >
              <Copy className="mr-2 h-4 w-4" />
              Clone
            </Button>
          )}
          {job.status === "failed" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { void handleRetry(); }}
              disabled={retryJob.isPending}
            >
              {retryJob.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Retry
            </Button>
          )}
          {ACTIVE_STATUSES.includes(job.status) && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => { void handleCancel(); }}
              disabled={cancelJob.isPending}
            >
              {cancelJob.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Square className="mr-2 h-4 w-4" />
              )}
              Cancel Import
            </Button>
          )}
        </div>
      </div>

      {/* Metadata */}
      <JobDetailsCard
        job={job}
        tenantId={tenantId}
        tenantName={tenantName ?? tenantId}
      />

      {job.export_csv && <ExportCsvSection job={job} tenantId={tenantId} />}

      {/* SD Import Progress */}
      {job.create_tickets && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            Service Desk Import
          </h3>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold">{job.fetched_tickets}</div>
                <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                  {job.status === "fetching" && <Loader2 className="h-3 w-3 animate-spin" />}
                  Fetched
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold">{job.succeeded_tickets}</div>
                <div className="text-sm text-muted-foreground">Succeeded</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className={`text-3xl font-bold ${job.failed_tickets_count > 0 ? "text-destructive" : ""}`}>
                  {job.failed_tickets_count}
                </div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold">{job.skipped_tickets}</div>
                <div className="text-sm text-muted-foreground">Skipped</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold">
                  {job.processed_tickets}
                </div>
                <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                  {ACTIVE_STATUSES.includes(job.status) && <Loader2 className="h-3 w-3 animate-spin" />}
                  Processed
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Insights Progress */}
      {job.generate_insights && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            KB Insights
          </h3>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold">{job.fetched_tickets}</div>
                <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                  {job.status === "fetching" && <Loader2 className="h-3 w-3 animate-spin" />}
                  Fetched
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold">
                  {job.insights_processed_tickets}
                </div>
                <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                  {ACTIVE_STATUSES.includes(job.status) && <Loader2 className="h-3 w-3 animate-spin" />}
                  Ingested
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-muted-foreground">
                  {job.fetched_tickets - job.insights_processed_tickets - job.insights_failed_tickets}
                </div>
                <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                  Skipped
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs">
                          Only tickets with a resolved, closed, done, or completed status
                          are eligible for insights. Other tickets are skipped.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className={`text-3xl font-bold ${job.insights_failed_tickets > 0 ? "text-destructive" : ""}`}>
                  {job.insights_failed_tickets}
                </div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Error message */}
      {job.error_message && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/5 p-4">
          <XCircle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
          <div>
            <p className="text-sm font-medium text-destructive">Job Error</p>
            <p className="mt-1 text-sm text-destructive/90">{job.error_message}</p>
          </div>
        </div>
      )}

      {/* Tickets list. An export records no per-ticket results. */}
      {job.processed_tickets > 0 && !job.export_csv && (
        <ImportTicketsList
          tenantId={tenantId}
          jobId={jobId}
          failedCount={job.failed_tickets_count}
          skippedCount={job.skipped_tickets}
          succeededCount={job.succeeded_tickets}
          isJobActive={ACTIVE_STATUSES.includes(job.status)}
        />
      )}
    </div>
  );
}

function JobDetailsCard({
  job,
  tenantId,
  tenantName,
}: {
  job: ImportJobResponse;
  tenantId: string;
  tenantName: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const isJiraProviderJob =
    job.source === "provider" &&
    supportsCustomFieldMappings(job.provider ?? undefined);
  // Only needed to resolve "inherit"; the current integration value, not a
  // snapshot of what the job ran with.
  const inheritedLinkedTickets = useIntegrationSyncLinkedTickets(
    tenantId,
    isJiraProviderJob && job.sync_linked_tickets == null
      ? (job.credential_id ?? undefined)
      : undefined,
  );

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm md:grid-cols-3">
          <div>
            <span className="text-muted-foreground">Tenant</span>
            <p>{tenantName}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Job ID</span>
            <p className="font-mono text-xs">{job.id}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Source</span>
            <p>
              {job.source}
              {job.provider && ` (${job.provider})`}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Targets</span>
            <p>
              {[
                job.export_csv && "CSV Export",
                job.create_tickets && "Service Desk",
                job.generate_insights && "KB Insights",
              ]
                .filter(Boolean)
                .join(" + ")}
            </p>
          </div>
          {job.target_desk_id && (
            <div>
              <span className="text-muted-foreground">Target Desk</span>
              <p>{job.target_desk_id}</p>
            </div>
          )}
          {job.target_subdesk_id && (
            <div>
              <span className="text-muted-foreground">Target Sub-desk</span>
              <p>{job.target_subdesk_id}</p>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Credential</span>
            <p className="font-mono text-xs">
              {job.credential_id ?? "—"}
            </p>
          </div>
          {job.rate_limit && (
            <div>
              <span className="text-muted-foreground">Rate Limit</span>
              <p>{job.rate_limit} req/s</p>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Skip Dedup</span>
            <p>{job.skip_dedup ? "Yes" : "No"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Created</span>
            <p>{format(new Date(job.created_at), "PPp")}</p>
          </div>
          {job.started_at && (
            <div>
              <span className="text-muted-foreground">Started</span>
              <p>{format(new Date(job.started_at), "PPp")}</p>
            </div>
          )}
          {job.started_at && (
            <div>
              <span className="text-muted-foreground">Duration</span>
              <p>
                {(() => {
                  const start = new Date(job.started_at);
                  const end = job.completed_at
                    ? new Date(job.completed_at)
                    : new Date();
                  const d = intervalToDuration({ start, end });
                  return [
                    String(d.hours ?? 0).padStart(2, "0"),
                    String(d.minutes ?? 0).padStart(2, "0"),
                    String(d.seconds ?? 0).padStart(2, "0"),
                  ].join(":");
                })()}
              </p>
            </div>
          )}
          {job.completed_at && (
            <div>
              <span className="text-muted-foreground">Completed</span>
              <p>{format(new Date(job.completed_at), "PPp")}</p>
            </div>
          )}
        </div>

        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-4 flex items-center gap-1 text-xs text-muted-foreground"
            >
              {expanded ? "Less" : "More"}
              <ChevronDown
                className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-3 border-t pt-3 text-sm md:grid-cols-3">
              <div>
                <span className="text-muted-foreground">
                  Source Project
                </span>
                <p>{job.source_project ?? "—"}</p>
              </div>
              {job.max_tickets && (
                <div>
                  <span className="text-muted-foreground">
                    Max Tickets
                  </span>
                  <p>{job.max_tickets}</p>
                </div>
              )}
              {job.since_date && (
                <div>
                  <span className="text-muted-foreground">Since</span>
                  <p>{format(new Date(job.since_date), "PP")}</p>
                </div>
              )}
              {job.until_date && (
                <div>
                  <span className="text-muted-foreground">Until</span>
                  <p>{format(new Date(job.until_date), "PP")}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">
                  Include Closed
                </span>
                <p>{job.include_closed ? "Yes" : "No"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Record Types</span>
                <p>{job.record_types?.length ? job.record_types.join(", ") : "Any"}</p>
              </div>
              {job.source === "provider" &&
                supportsCustomFieldMappings(job.provider ?? undefined) && (
                  <div className="col-span-full">
                    <span className="text-muted-foreground">Custom Fields</span>
                    <CustomFieldMappingsSummary
                      mappings={job.custom_field_mappings}
                    />
                  </div>
                )}
              {isJiraProviderJob && (
                <div>
                  <span className="text-muted-foreground">
                    Sync Linked Tickets
                  </span>
                  <p>
                    {job.sync_linked_tickets == null
                      ? inheritedLinkedTickets === undefined
                        ? "Inherit"
                        : `Inherit (integration currently ${
                            inheritedLinkedTickets ? "on" : "off"
                          })`
                      : job.sync_linked_tickets
                        ? "On"
                        : "Off"}
                  </p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Dry Run</span>
                <p>{job.dry_run ? "Yes" : "No"}</p>
              </div>
              {job.s3_keys && job.s3_keys.length > 0 && (
                <div className="col-span-full">
                  <span className="text-muted-foreground">
                    S3 Keys
                  </span>
                  <ul className="mt-1 list-inside list-disc font-mono text-xs">
                    {job.s3_keys.map((key) => (
                      <li key={key}>{key}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="col-span-full">
                <span className="text-muted-foreground">
                  Transform Code
                </span>
                {job.transform_code ? (
                  <pre className="mt-1 max-h-40 overflow-auto rounded bg-muted p-2 font-mono text-xs">
                    {job.transform_code}
                  </pre>
                ) : (
                  <p>—</p>
                )}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
